// components/OBE/CoPoMapping.jsx
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { 
  FiBook, 
  FiGitBranch, 
  FiCalendar, 
  FiLink, 
  FiInfo, 
  FiDownload,
  FiFileText,
  FiFilter,
  FiAlertCircle,
  FiCpu
} from "react-icons/fi";
import { baseApiURL } from "../../../baseUrl";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const CoPoMapping = ({ branch: lockedBranchName }) => {
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branch: "",
    semester: "",
    regulation: ""
  });
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [coPoMappings, setCoPoMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPOInfo, setShowPOInfo] = useState(false);
  const [coAttainments, setCoAttainments] = useState([]);
  const [poAttainments, setPoAttainments] = useState([]);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");

  // Program Outcomes Data
  const poData = [
    { poNumber: 'PO1', title: 'Engineering knowledge', description: 'An ability to apply knowledge of mathematics (including probability, statistics and discrete mathematics), science, and engineering for solving Engineering problems and Knowledge.' },
    { poNumber: 'PO2', title: 'Problem analysis', description: 'An ability to design, simulate and conduct experiments, as well as to analyze and interpret data including hardware and software components.' },
    { poNumber: 'PO3', title: 'Design / development of solutions', description: 'An ability to design a complex electronic system or process to meet desired specifications and needs.' },
    { poNumber: 'PO4', title: 'Conduct investigations of complex Problem', description: 'An ability to identify, formulate, comprehend, analyze, design synthesis of the information to solve complex engineering problems and provide valid conclusions.' },
    { poNumber: 'PO5', title: 'Modern tool usage', description: 'An ability to use the techniques, skills and modern engineering tools necessary for engineering practice' },
    { poNumber: 'PO6', title: 'The engineer and society', description: 'An understanding of professional, health, safety, legal,' },
    { poNumber: 'PO7', title: 'Environment and sustainability', description: 'The broad education necessary to understand the impact of engineering solutions in a global, economic, environmental and demonstrate the knowledge need for sustainable development' },
    { poNumber: 'PO8', title: 'Ethics', description: 'Apply ethical principles, responsibility and norms of the engineering practice.' },
    { poNumber: 'PO9', title: 'Individual and team work', description: 'An ability to function on multi-disciplinary teams.' },
    { poNumber: 'PO10', title: 'Communication', description: 'An ability to communicate and present effectively' },
    { poNumber: 'PO11', title: 'Project management and finance', description: 'An ability to use the modern engineering tools, techniques, skills and management principles to do work as a member and leader in a team, to manage projects in multi-disciplinary environments' },
    { poNumber: 'PO12', title: 'Life-long learning', description: 'A recognition of the need for, and an ability to engage in, to resolve contemporary issues and acquire lifelong learning' }
  ];

  const [studentRegulations, setStudentRegulations] = useState([]);

  useEffect(() => {
    getSubjectsHandler();
    getBranchesHandler();
    getStudentRegulationsHandler();
  }, []);

  const getStudentRegulationsHandler = () => {
    axios
      .get(`${baseApiURL()}/student/details/getRegulations`)
      .then((response) => {
        if (response.data.success) {
          setStudentRegulations(response.data.regulations || []);
        }
      })
      .catch((error) => console.error("Error fetching student regulations:", error));
  };

  useEffect(() => {
    filterSubjects();
  }, [subjects, filters]);

  useEffect(() => {
    checkStudentPresence();
  }, [filters.semester, filters.branch, branches, selectedSubject]);

  const checkStudentPresence = async () => {
    const targetSemester = filters.semester || selectedSubject?.semester;
    if (!targetSemester) {
      setNoStudentsMessage("");
      return;
    }

    try {
      let bName = "";
      if (filters.branch) {
        const branchObj = branches.find(b => b._id === filters.branch || b.name === filters.branch);
        if (branchObj?.name) bName = branchObj.name;
      } else if (selectedSubject?.branch) {
        bName = selectedSubject.branch?.name || selectedSubject.branch;
      }

      const response = await axios.post(`${baseApiURL()}/student/details/getCohortRegulation`, {
        semester: Number(targetSemester),
        branch: bName
      });

      if (response.data.success && response.data.count > 0 && response.data.regulations?.length > 0) {
        const studentReg = response.data.regulation || response.data.regulations[0];
        setFilters(prev => ({ ...prev, regulation: studentReg }));
        setNoStudentsMessage("");
      } else {
        setFilters(prev => ({ ...prev, regulation: "" }));
        setNoStudentsMessage("no student in that semester");
      }
    } catch (error) {
      console.error("CoPoMapping checkStudentPresence error:", error);
      setFilters(prev => ({ ...prev, regulation: "" }));
      setNoStudentsMessage("no student in that semester");
    }
  };

  const getBranchesHandler = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((response) => {
        if (response.data.success) {
          setBranches(response.data.branches);
          if (lockedBranchName) {
            const matchedBranch = response.data.branches.find(b => b.name === lockedBranchName);
            if (matchedBranch) {
              setFilters(prev => ({ ...prev, branch: matchedBranch._id }));
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching branches:", error);
      });
  };

  const filterSubjects = () => {
    if (noStudentsMessage) {
      setFilteredSubjects([]);
      return;
    }
    let filtered = [...subjects];
    
    if (filters.branch) {
      filtered = filtered.filter(item => {
        const branchId = item.branch?._id || item.branch;
        return branchId === filters.branch;
      });
    }
    
    if (filters.semester) {
      filtered = filtered.filter(item => 
        String(item.semester) === String(filters.semester)
      );
    }

    if (filters.regulation) {
      filtered = filtered.filter(item => 
        item.regulation && item.regulation.toUpperCase() === filters.regulation.toUpperCase()
      );
    }
    
    setFilteredSubjects(filtered);
  };

  const clearFilters = () => {
    setFilters({
      branch: lockedBranchName ? filters.branch : "",
      semester: "",
      regulation: ""
    });
  };

  const getSubjectsHandler = () => {
    setLoading(true);
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((response) => {
        if (response.data.success) {
          setSubjects(response.data.subject);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setLoading(true);
    
    // Fetch both CO-PO mappings and attainments in parallel
    Promise.all([
      axios.get(`${baseApiURL()}/subject/getCoPoMappings/${subject._id}`),
      axios.get(`${baseApiURL()}/coattainment/attainments/${subject._id}`)
    ])
      .then(([mappingResponse, attainmentResponse]) => {
        if (mappingResponse.data.success) {
          setCoPoMappings(mappingResponse.data.coPoMatrix || {});
        } else {
          toast.error(mappingResponse.data.message);
        }
        
        if (attainmentResponse.data.success) {
          const coAtt = attainmentResponse.data.coAttainments || [];
          setCoAttainments(coAtt);

          // Compute PO attainments locally using weighted-average formula:
          // PO = Σ(CO_attainment × strength) / Σ(strength)
          const computedPo = computePoFromCoAttainments(coAtt, mappingResponse.data.coPoMatrix || {});
          setPoAttainments(computedPo);

          // Also set any server-provided PO attainments for reference (optional)
          // setPoAttainments(attainmentResponse.data.poAttainments || computedPo);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        toast.error(error.response?.data?.message || error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleStrengthChange = async (coNumber, poNumber) => {
    if (!selectedSubject) return;

    const currentStrength = coPoMappings[coNumber]?.[poNumber] || null;
    let nextStrength;
    
    // Cycle through: null → 3 → 2 → 1 → null
    if (currentStrength === null) nextStrength = 3;
    else if (currentStrength === 3) nextStrength = 2;
    else if (currentStrength === 2) nextStrength = 1;
    else nextStrength = null;

    try {
      const response = await axios.put(
        `${baseApiURL()}/subject/updateCoPoMapping/${selectedSubject._id}`,
        { 
          coNumber, 
          poNumber, 
          strength: nextStrength 
        }
      );

      if (response.data.success) {
        // Update local state
        const updatedMappings = { ...coPoMappings };
        if (!updatedMappings[coNumber]) updatedMappings[coNumber] = {};
        updatedMappings[coNumber][poNumber] = nextStrength;
        setCoPoMappings(updatedMappings);
        
        // Update attainments if returned from server
        if (response.data.poAttainments) {
          setPoAttainments(response.data.poAttainments);
        }
        
        toast.success("Mapping updated successfully");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 3: return 'bg-green-500 hover:bg-green-600';
      case 2: return 'bg-yellow-500 hover:bg-yellow-600';
      case 1: return 'bg-red-500 hover:bg-red-600';
      default: return 'bg-gray-200 hover:bg-gray-300';
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 3: return 'Strong';
      case 2: return 'Medium';
      case 1: return 'Weak';
      default: return 'Not Mapped';
    }
  };

  const exportToExcel = () => {
    if (!selectedSubject || !selectedSubject.courseOutcomes?.length) {
      toast.error("No data available to export");
      return;
    }

    try {
      // Prepare worksheet data
      const wsData = [];
      
      // Header row 1: PO Numbers
      const headerRow1 = ['CO Number', 'CO Description'];
      poData.forEach(po => {
        headerRow1.push(`${po.poNumber}`);
      });
      wsData.push(headerRow1);
      
      // Header row 2: PO Titles (abbreviated)
      const headerRow2 = ['', ''];
      poData.forEach(po => {
        const shortTitle = po.title.split(' ').slice(0, 2).join(' ');
        headerRow2.push(shortTitle);
      });
      wsData.push(headerRow2);
      
      // Data rows for each CO
      selectedSubject.courseOutlements.forEach(co => {
        const row = [
          co.coNumber,
          co.description || ''
        ];
        
        poData.forEach(po => {
          const strength = coPoMappings[co.coNumber]?.[po.poNumber] || null;
          let strengthText = '';
          if (strength === 3) strengthText = 'Strong (3)';
          else if (strength === 2) strengthText = 'Medium (2)';
          else if (strength === 1) strengthText = 'Weak (1)';
          else strengthText = 'Not Mapped';
          
          row.push(strengthText);
        });
        
        wsData.push(row);
      });
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      const colWidths = [
        { wch: 12 }, // CO Number
        { wch: 60 }, // CO Description
        ...poData.map(() => ({ wch: 15 })) // PO columns
      ];
      ws['!cols'] = colWidths;
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'CO-PO Mapping');
      
      // Add summary sheet
      const summaryData = [
        ['Subject Information', ''],
        ['Subject Code', selectedSubject.code],
        ['Subject Name', selectedSubject.name],
        ['Branch', selectedSubject.branch?.name || 'N/A'],
        ['Semester', selectedSubject.semester],
        ['Date Exported', new Date().toLocaleDateString()],
        ['', ''],
        ['Mapping Summary', ''],
        ['Strong Mappings', Object.keys(coPoMappings).reduce((acc, co) => {
          return acc + Object.values(coPoMappings[co]).filter(val => val === 3).length;
        }, 0)],
        ['Medium Mappings', Object.keys(coPoMappings).reduce((acc, co) => {
          return acc + Object.values(coPoMappings[co]).filter(val => val === 2).length;
        }, 0)],
        ['Weak Mappings', Object.keys(coPoMappings).reduce((acc, co) => {
          return acc + Object.values(coPoMappings[co]).filter(val => val === 1).length;
        }, 0)],
        ['Total Mappings', Object.keys(coPoMappings).reduce((acc, co) => {
          return acc + Object.values(coPoMappings[co]).filter(val => val !== null).length;
        }, 0)],
        ['Total Possible Mappings', selectedSubject.courseOutcomes?.length * 12],
        ['Mapping Coverage', `${((Object.keys(coPoMappings).reduce((acc, co) => {
          return acc + Object.values(coPoMappings[co]).filter(val => val !== null).length;
        }, 0) / (selectedSubject.courseOutcomes?.length * 12)) * 100).toFixed(1)}%`]
      ];
      
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      
      // Generate Excel file
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
      
      // Convert to blob and save
      const buf = new ArrayBuffer(wbout.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xFF;
      
      const blob = new Blob([buf], { type: 'application/octet-stream' });
      
      const fileName = `CO-PO_Mapping_${selectedSubject.code}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      
      toast.success("Excel file exported successfully!");
      
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToCSV = () => {
    if (!selectedSubject || !selectedSubject.courseOutcomes?.length) {
      toast.error("No data available to export");
      return;
    }

    try {
      // Prepare CSV data
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Header
      const headers = ['CO Number', 'CO Description', ...poData.map(po => po.poNumber)];
      csvContent += headers.join(',') + '\r\n';
      
      // Data rows
      selectedSubject.courseOutcomes.forEach(co => {
        const row = [
          `"${co.coNumber}"`,
          `"${(co.description || '').replace(/"/g, '""')}"`,
          ...poData.map(po => {
            const strength = coPoMappings[co.coNumber]?.[po.poNumber] || null;
            return strength !== null ? strength : '';
          })
        ];
        csvContent += row.join(',') + '\r\n';
      });
      
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `CO-PO_Mapping_${selectedSubject.code}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("CSV file exported successfully!");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      toast.error("Failed to export CSV file");
    }
  };

  const handleAutoMapCOs = async () => {
    if (!selectedSubject) {
      toast.error("Please select a subject first");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${baseApiURL()}/subject/autoMapCoPo/${selectedSubject._id}`);
      if (response.data.success) {
        toast.success(response.data.message || "Automatic CO-PO mapping completed!");
        // Refresh subject mappings
        handleSubjectSelect(selectedSubject);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error auto-mapping COs:", error);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Compute PO attainments locally using weighted average formula
  const computePoFromCoAttainments = (coAttainmentsArr, coPoMatrix) => {
    // po keys PO1..PO12
    const poKeys = Array.from({ length: 12 }, (_, i) => `PO${i + 1}`);
    const result = [];

    poKeys.forEach((po) => {
      let totalAttainment = 0;
      let mappedCount = 0;

      (coAttainmentsArr || []).forEach((coObj) => {
        const coNum = coObj.coNumber;
        const coAtt = Number(coObj.attainment) || 0;
        const strength = (coPoMatrix && coPoMatrix[coNum]) ? coPoMatrix[coNum][po] : null;
        if (strength !== null && typeof strength !== 'undefined') {
          const s = Number(strength) || 0;
          if (s > 0) {
            totalAttainment += (coAtt * s) / 3;
            mappedCount += s;
          }
        }
      });

      const attainment = mappedCount > 0 ? parseFloat(((totalAttainment / mappedCount) * 100).toFixed(2)) : 0;
      result.push({ poNumber: po, attainment });
    });

    return result;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bento-header-banner flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">CO-PO Mapping</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Map Course Outcomes to Program Outcomes</p>
        </div>
        <div className="flex items-center space-x-2">
          {selectedSubject && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleAutoMapCOs}
                disabled={loading}
                className="flex items-center space-x-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
                title="Automatically map COs to POs using NLP keyword matching"
              >
                <FiCpu size={16} />
                <span>Auto Map COs</span>
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
              >
                <FiFileText size={16} />
                <span>Export CSV</span>
              </button>
            </div>
          )}
          <button
            onClick={() => setShowPOInfo(!showPOInfo)}
            className="flex items-center space-x-1.5 px-3.5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 transition cursor-pointer"
          >
            <FiInfo size={16} />
            <span>PO Details</span>
          </button>
        </div>
      </div>

      {/* PO Info Modal */}
      {showPOInfo && (
        <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center text-white">
              <h2 className="text-sm font-bold uppercase tracking-wider">Program Outcomes (POs) Details</h2>
              <button
                onClick={() => setShowPOInfo(false)}
                className="text-white hover:text-slate-200 text-lg font-bold cursor-pointer"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-3">
              {poData.map((po) => (
                <div key={po.poNumber} className="border-l-4 border-indigo-600 pl-4 py-2.5 bg-slate-50 dark:bg-slate-950 rounded-r-xl">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-bold text-xs text-indigo-600 dark:text-indigo-400 font-mono">{po.poNumber}</span>
                    <span className="font-bold text-xs text-slate-900 dark:text-white">{po.title}</span>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">{po.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Column - Subject Selection */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
            <div className="flex items-center space-x-2">
              <FiBook className="text-indigo-600 dark:text-indigo-400 text-base" />
              <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Select Subject</h2>
            </div>

            {/* Filters Section */}
            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiFilter className="text-indigo-600 dark:text-indigo-400 text-xs" />
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Filter Subjects</span>
                </div>
                {(filters.branch || filters.semester || filters.regulation) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-semibold cursor-pointer"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Filter by Branch</label>
                <select
                  value={filters.branch}
                  onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                  disabled={!!lockedBranchName}
                  className={`w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white ${lockedBranchName ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800' : 'bg-white dark:bg-slate-900'}`}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Filter by Semester</label>
                <select
                  value={filters.semester}
                  onChange={(e) => setFilters({ ...filters, semester: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                >
                  <option value="">All Semesters</option>
                  {semesters.map((sem) => (
                    <option key={sem} value={sem}>
                      Semester {sem}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Filter by Regulation</label>
                  {filters.regulation && (
                    <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded font-bold border border-indigo-100 dark:border-indigo-800">
                      Auto-fetched
                    </span>
                  )}
                </div>
                <select
                  value={filters.regulation}
                  onChange={(e) => setFilters({ ...filters, regulation: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                >
                  <option value="">All Regulations</option>
                  {(studentRegulations.length > 0 ? studentRegulations : Array.from(new Set(subjects.map(s => s.regulation).filter(Boolean)))).map((reg) => (
                    <option key={reg} value={reg}>
                      {reg}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-1 font-medium">
                Showing {filteredSubjects.length} of {subjects.length} subjects
              </div>

              {noStudentsMessage && (
                <div className="mt-2 p-2.5 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-2">
                  <FiAlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="font-semibold">{noStudentsMessage}</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[450px] overflow-y-auto pr-1">
                {filteredSubjects.map((subject) => (
                  <div
                    key={subject._id}
                    className={`p-3.5 rounded-xl border cursor-pointer transition ${
                      selectedSubject?._id === subject._id
                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30'
                        : 'border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
                    }`}
                    onClick={() => handleSubjectSelect(subject)}
                  >
                    <h3 className="font-bold text-xs text-slate-900 dark:text-white">{subject.code} - {subject.name}</h3>
                    <div className="flex items-center space-x-3 mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                      <div className="flex items-center space-x-1">
                        <FiGitBranch className="text-emerald-500" />
                        <span>{subject.branch?.name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FiCalendar className="text-indigo-500" />
                        <span>Sem {subject.semester}</span>
                      </div>
                      {subject.regulation && (
                        <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[10px] font-bold border border-indigo-100 dark:border-indigo-800">
                          {subject.regulation}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex justify-between">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg">
                        {subject.courseOutcomes?.length || 0} COs
                      </span>
                      {subject.coPoMappings?.length > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded-lg border border-emerald-100 dark:border-emerald-800">
                          {subject.coPoMappings.length} Mappings
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                
                {filteredSubjects.length === 0 && (
                  <div className="text-center py-8">
                    <FiBook className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No subjects found matching your filters.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-3">
            <div className="flex items-center space-x-2">
              <FiLink className="text-indigo-600 dark:text-indigo-400 text-base" />
              <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Mapping Legend</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
              <div className="flex items-center space-x-2 p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <div className="w-6 h-6 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  3
                </div>
                <span className="text-emerald-800 dark:text-emerald-300">Strong</span>
              </div>
              <div className="flex items-center space-x-2 p-2 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
                <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  2
                </div>
                <span className="text-amber-800 dark:text-amber-300">Medium</span>
              </div>
              <div className="flex items-center space-x-2 p-2 bg-rose-50 dark:bg-rose-950/40 rounded-xl border border-rose-200 dark:border-rose-800">
                <div className="w-6 h-6 bg-rose-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  1
                </div>
                <span className="text-rose-800 dark:text-rose-300">Weak</span>
              </div>
              <div className="flex items-center space-x-2 p-2 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="w-6 h-6 bg-slate-300 dark:bg-slate-700 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-300 font-bold text-xs">
                  -
                </div>
                <span className="text-slate-600 dark:text-slate-400">None</span>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-400 dark:text-slate-500 space-y-1 font-medium">
              <p>• Click on any cell to cycle through mapping strengths.</p>
              <p>• Use Export buttons to download data in CSV format.</p>
            </div>
          </div>
        </div>

        {/* Right Column - CO-PO Matrix */}
        <div className="lg:col-span-3">
          {selectedSubject ? (
            <div className="space-y-6">
              {noStudentsMessage && (
                <div className="p-3.5 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center space-x-3 text-amber-800 dark:text-amber-300 shadow-xs">
                  <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Notice</p>
                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">{noStudentsMessage}</p>
                  </div>
                </div>
              )}

              {/* Subject Info Banner */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-base font-bold text-slate-900 dark:text-white">
                      {selectedSubject.code} - {selectedSubject.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
                      <span>
                        <FiGitBranch className="inline mr-1 text-emerald-500" />
                        {selectedSubject.branch?.name}
                      </span>
                      <span>
                        <FiCalendar className="inline mr-1 text-indigo-500" />
                        Semester {selectedSubject.semester}
                      </span>
                      {selectedSubject.regulation && (
                        <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded text-[10px] font-bold border border-indigo-100 dark:border-indigo-800">
                          {selectedSubject.regulation}
                        </span>
                      )}
                      <span>
                        <FiLink className="inline mr-1 text-purple-500" />
                        {selectedSubject.courseOutcomes?.length || 0} Course Outcomes
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-400 dark:text-slate-500 font-semibold">Total Mappings</div>
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {Object.keys(coPoMappings).reduce((acc, co) => {
                        return acc + Object.values(coPoMappings[co]).filter(val => val !== null).length;
                      }, 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* CO-PO Mapping Matrix */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? (
                  <div className="flex justify-center items-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : selectedSubject.courseOutcomes?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs" id="co-po-table">
                      <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3.5 text-left">
                            CO \ PO
                          </th>
                          {poData.map((po) => (
                            <th key={po.poNumber} className="px-2 py-3.5 text-center">
                              <div className="flex flex-col items-center">
                                <span className="font-bold text-indigo-600 dark:text-indigo-400 font-mono">{po.poNumber}</span>
                                <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-[60px]">
                                  {po.title.split(' ')[0]}
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                        {selectedSubject.courseOutcomes.map((co) => {
                          return (
                            <tr key={co.coNumber} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800 rounded-lg flex items-center justify-center font-bold text-xs shrink-0">
                                    {co.coNumber}
                                  </div>
                                  <div className="ml-3 max-w-xs">
                                    <div className="text-xs font-bold text-slate-900 dark:text-white">{co.coNumber}</div>
                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                      {co.description}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              {poData.map((po) => {
                                const strength = coPoMappings[co.coNumber]?.[po.poNumber] || null;
                                return (
                                  <td key={`${co.coNumber}-${po.poNumber}`} className="px-2 py-3 text-center">
                                    <button
                                      onClick={() => handleStrengthChange(co.coNumber, po.poNumber)}
                                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs mx-auto transition cursor-pointer shadow-xs ${getStrengthColor(
                                        strength
                                      )}`}
                                      title={`${co.coNumber} ↔ ${po.poNumber}\n${getStrengthText(strength)}\nClick to change`}
                                    >
                                      {strength || '_'}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FiLink className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">No Course Outcomes</h3>
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      This subject doesn't have any Course Outcomes defined yet.
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                      Add Course Outcomes first in the Course Outcomes tab.
                    </p>
                  </div>
                )}
              </div>

              {/* Summary Statistics */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
                <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Mapping Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3.5">
                    <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                      {Object.keys(coPoMappings).reduce((acc, co) => {
                        return acc + Object.values(coPoMappings[co]).filter(val => val === 3).length;
                      }, 0)}
                    </div>
                    <div className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5">Strong Mappings</div>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl p-3.5">
                    <div className="text-xl font-bold text-amber-700 dark:text-amber-300">
                      {Object.keys(coPoMappings).reduce((acc, co) => {
                        return acc + Object.values(coPoMappings[co]).filter(val => val === 2).length;
                      }, 0)}
                    </div>
                    <div className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 mt-0.5">Medium Mappings</div>
                  </div>
                  <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl p-3.5">
                    <div className="text-xl font-bold text-rose-700 dark:text-rose-300">
                      {Object.keys(coPoMappings).reduce((acc, co) => {
                        return acc + Object.values(coPoMappings[co]).filter(val => val === 1).length;
                      }, 0)}
                    </div>
                    <div className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 mt-0.5">Weak Mappings</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3.5">
                    <div className="text-xl font-bold text-slate-700 dark:text-slate-300">
                      {selectedSubject.courseOutcomes?.length * 12 - 
                       Object.keys(coPoMappings).reduce((acc, co) => {
                         return acc + Object.values(coPoMappings[co]).filter(val => val !== null).length;
                       }, 0)}
                    </div>
                    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">Unmapped</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-12 text-center">
              <FiLink className="w-16 h-16 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">Select a Subject</h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 max-w-md mx-auto">
                Choose a subject from the list to view and manage CO-PO mappings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoPoMapping;